import { query } from '@angular/animations';
import { CommonModule, DatePipe, JsonPipe, ɵnormalizeQueryParams } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { IgxGridComponent, IgxTabsModule } from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import {
  IDateRangeForQuery,
  IPaymentReportDisplay,
  IPaymentReportQuery,
  IPaymentReportResult,
} from 'src/app/shared/interfaces';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';
import { DateSelectionComponent } from '../../../shared/ui/date-selection/date-selection.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-report-payment',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    IgxGridModule,
    IgxTabsModule,
    IgxGridComponent,
    HeaderTitleLotDataComponent,
    MatSnackBarModule,
    DateSelectionComponent,
    MatProgressBarModule,
  ],
  templateUrl: './report-payment.component.html',
  styleUrl: './report-payment.component.scss',
})
export class ReportPaymentComponent implements OnInit {
  paymentReportData: IPaymentReportResult[] = [];
  paymentReportDisplayData: IPaymentReportDisplay[] = [];
  urlDomain: string = '';
  dateRangeForQuery: IDateRangeForQuery = {} as IDateRangeForQuery;
  queryInProcess: boolean = false;
  queryCompleted: boolean = false;

  paymentSearchForm = new FormGroup({
    paymentCode: new FormControl('0', []),
    tenantName: new FormControl('', []),
  });

  //Services
  public _appGeneralState = inject(AppGeneralStateService);
  _dataService = inject(DataService);
  _destroyRefService = inject(DestroyRef);
  _messageService = inject(MessageDialogService);
  _datePipe = inject(DatePipe);
  private readonly router = inject(Router);

  constructor() {
    //Init dateRanges
    this.dateRangeForQuery.startDate = new Date();
    this.dateRangeForQuery.endDate = new Date();
    this.urlDomain = environment.UrlDomain;
  }

  ngOnInit(): void {
    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });
  }

  onLotNumberChange(lotNumber: string): void {
    this.onClearSearchCriteria();
  }

  onClearSearchCriteria(): void {
    this.paymentSearchForm.patchValue({
      tenantName: '',
      paymentCode: '0',
    });

    this._appGeneralState.resetDateSelection();

    //Clear data
    this.paymentReportData = [];
    this.paymentReportDisplayData = [];
  }

  onFindInvoicePayments(): void {
    //Clear out existing data
    this.paymentReportData = [];
    this.paymentReportDisplayData = [];

    const lotNumber: string = this._appGeneralState.currentLotNumber;
    var queryData: IPaymentReportQuery = {} as IPaymentReportQuery;
    queryData.fromDate = this.dateRangeForQuery.startDate;
    queryData.toDate = this.dateRangeForQuery.endDate;
    queryData.paymentTypeId = +this.paymentSearchForm.get('paymentCode').value;
    queryData.tenantName = this.paymentSearchForm.get('tenantName').value;
    queryData.lotNumber = lotNumber;

    this.getPaymentData(queryData);
  }

  getPaymentData(queryString: IPaymentReportQuery) {
    this.queryInProcess = true;
    this.queryCompleted = false;

    this._dataService
      .getReportPaymentData(queryString)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.paymentReportData = res;
        },
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
          this._messageService.snackBarTopCenterTimed(
            'An unexpected error occurred while loading the Payment report.  Please try again.',
            5000,
          );
        },
        complete: () => {
          this.queryInProcess = false;
          this.queryCompleted = true;
          this.prepDisplayData();
        },
      });
  }

  onHandleQueryData(dateData: IDateRangeForQuery): void {
    this.dateRangeForQuery = dateData;
  }

  prepDisplayData(): void {
    var ii: number = 0;
    //this._datePipe.transform(leaseData.effectiveDate, 'MM/dd/yyyy'),

    for (ii = 0; ii < this.paymentReportData.length; ii++) {
      var newPayment: IPaymentReportDisplay = {} as IPaymentReportDisplay;

      newPayment.invoicePaymentId = this.paymentReportData[ii].invoicePaymentId;
      newPayment.invoiceNumber = this.paymentReportData[ii].invoiceNumber;
      newPayment.revenueDate = this._datePipe.transform(this.paymentReportData[ii].revenueDate, 'MM/dd/yyyy');
      newPayment.dueDate = this._datePipe.transform(this.paymentReportData[ii].dueDate, 'MM/dd/yyyy');
      newPayment.depositDate = this._datePipe.transform(this.paymentReportData[ii].depositDate, 'MM/dd/yyyy');
      newPayment.transactionDate = this._datePipe.transform(this.paymentReportData[ii].transactionDate, 'MM/dd/yyyy');
      newPayment.companyName = this.paymentReportData[ii].companyName;
      newPayment.parkerLastName = this.paymentReportData[ii].parkerLastName;
      newPayment.parkerFirstName = this.paymentReportData[ii].parkerFirstName;
      newPayment.amount = this.paymentReportData[ii].amount;
      newPayment.paymentCode = this.paymentReportData[ii].paymentCode;
      newPayment.referenceNumber = this.paymentReportData[ii].referenceNumber;
      newPayment.userName = this.paymentReportData[ii].userName;
      newPayment.comments = this.paymentReportData[ii].comments;
      newPayment.parkerPayType = this.paymentReportData[ii].parkerPayType;
      newPayment.customer = this.paymentReportData[ii].customer;
      newPayment.paymentScheduleType = this.paymentReportData[ii].paymentScheduleType;
      newPayment.filterDescription = this.paymentReportData[ii].filterDescription;
      newPayment.customerType = this.paymentReportData[ii].customerType;
      newPayment.lotNumber = this.paymentReportData[ii].lotNumber;

      this.paymentReportDisplayData.push(newPayment);
    }
  }

  onDisplayInvoice(invoiceNumber: string, lotNumber: string, entityId: string): void {
    const safeEntityId = entityId && entityId.trim().length > 0 ? entityId : 'na';
    const invoiceViewUrl = this.router.serializeUrl(
      this.router.createUrlTree(['/viewinvoice', safeEntityId, lotNumber, invoiceNumber]),
    );
    window.open(invoiceViewUrl, '_blank');
  }
}
